// src/components/dashboard/SendToTeamMenu.jsx
//
// One CTA, two destinations. Replaces the old twin "Team Chat" +
// "New Message" buttons that made coaches hesitate. Tap it, get a
// dropdown with named options. Today: 2 (chat / AI broadcast).
// Future-proof — add items to OPTIONS, no caller changes needed.
//
// Variants:
//   - 'primary' (default): lime-filled button on light backgrounds
//     (dashboard header)
//   - 'darkOutlined': lime-outlined button for use on pitch-dark
//     hero strips (team detail page hero)
//
// Sizes: 'sm' / 'md' / 'lg' — matches the LimeButton size pattern.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  PaperAirplaneIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

const SIZES = {
  sm: 'min-h-[32px] px-3 text-[11px] gap-1.5',
  md: 'min-h-[44px] px-4 text-[13px] gap-2',
  lg: 'min-h-[52px] px-5 text-[15px] gap-2',
}

const VARIANTS = {
  primary: 'bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14]',
  darkOutlined: 'border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#b5f03d]',
}

export default function SendToTeamMenu({
  teamId,
  disabled = false,
  size = 'md',
  variant = 'primary',
  label = 'Send to Team',
  // Anchors the dropdown's edge to the button's edge. Use 'left'
  // when the button sits on the left side of its row (dashboard
  // header) so the menu extends rightward into the viewport. Use
  // 'right' when the button is right-aligned (team detail hero,
  // table-row action menus) so the menu extends leftward.
  align = 'left',
}) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  // Position the menu in viewport coordinates so it can render via a
  // portal to document.body. Pre-fix the dropdown was an absolutely-
  // positioned child of the button wrapper, which got clipped by any
  // ancestor with overflow:hidden — notably the team page's dark
  // pitch hero strip, which clipped the dropdown wherever it extended
  // below the hero. Using fixed-positioning + portal escapes ALL
  // ancestor overflow / transform / contain stacking contexts.
  const [menuPos, setMenuPos] = useState(null)

  // Menu width must match the className below (w-[300px] sm:w-[320px]
  // max-w-[calc(100vw-2rem)]). We compute the actual rendered width at
  // position time so clamping is correct even when the viewport cap kicks in.
  const updateMenuPos = useCallback(() => {
    const btn = buttonRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const vw = window.innerWidth
    const PAD = 8 // viewport-edge padding

    // Resolve menu width: prefer the live measurement once the menu has
    // mounted (handles sm: breakpoint + max-w cap); fall back to 300 for
    // the first paint.
    const measuredWidth = menuRef.current ? menuRef.current.offsetWidth : 0
    const menuW = Math.min(
      measuredWidth || (vw >= 640 ? 320 : 300),
      vw - 2 * PAD,
    )

    // Compute the desired LEFT position, then clamp within the viewport.
    // Pre-fix this used a CSS `right` anchor that pushed the menu off-
    // screen whenever the button sat on the left side of a narrow
    // viewport (e.g., SEND button on team header at <640px width — flex
    // wrapped it to the left edge, but align="right" still tried to
    // pin the menu's right edge to the button's right edge, putting
    // most of the menu's body off the left side of the screen).
    const preferredLeft = align === 'right'
      ? rect.right - menuW   // menu's right edge meets button's right edge
      : rect.left            // menu's left edge meets button's left edge
    const left = Math.max(PAD, Math.min(preferredLeft, vw - menuW - PAD))

    setMenuPos({
      top: rect.bottom + 8, // 8px gap matching the original `mt-2`
      left,
    })
  }, [align])

  // When opening, snapshot position once. Re-snapshot on scroll +
  // resize so the menu stays anchored to the button if the page
  // layout shifts. Outside-click + Escape close as before, BUT the
  // outside check now considers BOTH refs (button + menu) since the
  // menu lives in document.body via portal — a click on a menu item
  // would otherwise count as "outside" and close the menu before
  // navigation fires.
  useEffect(() => {
    if (!open) return
    updateMenuPos()
    const onDocClick = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target)
      const inMenu   = menuRef.current   && menuRef.current.contains(e.target)
      if (!inButton && !inMenu) setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    const onReposition = () => updateMenuPos()
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
  }, [open, updateMenuPos])

  const OPTIONS = [
    {
      id: 'chat',
      icon: ChatBubbleLeftRightIcon,
      title: 'Post in Team Chat',
      desc: 'Quick update to your team channel. Free for everyone.',
      to: teamId ? `/teams/${teamId}/chat` : '/messages',
    },
    {
      id: 'ai-broadcast',
      icon: SparklesIcon,
      title: 'AI Broadcast',
      desc: 'Auto-drafts a bilingual message. Sends via SMS + email.',
      to: teamId ? `/teams/${teamId}/messages/new` : '/billing/upgrade',
    },
  ]

  const handlePick = (option) => {
    setOpen(false)
    navigate(option.to)
  }

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className={[
          'inline-flex items-center justify-center rounded-[10px] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
          SIZES[size] || SIZES.md,
          VARIANTS[variant] || VARIANTS.primary,
        ].join(' ')}
        style={{ fontFamily: HEAD_FONT, fontWeight: 800, letterSpacing: '0.6px' }}
      >
        <PaperAirplaneIcon className={iconSize} />
        {label}
        <ChevronDownIcon className={[iconSize, 'transition-transform', open ? 'rotate-180' : ''].join(' ')} />
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          // Fixed-positioned + rendered to document.body via portal so
          // ancestor overflow:hidden / transform / will-change can't
          // clip it. z-50 keeps it above app chrome (sticky headers,
          // sidebar, sub-nav). Width caps at viewport-2rem so it
          // doesn't push off-screen on narrow phones.
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 50,
          }}
          className="w-[300px] sm:w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white border border-zinc-200 shadow-[0_8px_24px_-4px_rgba(13,31,20,0.18)] overflow-hidden"
        >
          <div className="px-4 py-2 border-b border-zinc-100 bg-[#f7faf5]">
            <p
              className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              How do you want to reach them?
            </p>
          </div>

          <ul>
            {OPTIONS.map((opt) => {
              const Icon = opt.icon
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handlePick(opt)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#f7faf5] active:bg-[#b5f03d]/10 transition-colors group"
                  >
                    <div className="flex-shrink-0 h-9 w-9 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center group-hover:bg-[#b5f03d]/25 transition-colors">
                      <Icon className="h-4 w-4 text-[#5a8a1f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#0d1f14] leading-tight">{opt.title}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{opt.desc}</p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>,
        document.body
      )}
    </div>
  )
}
